import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Label,
    Input,
    CustomInput,
    Button,
    Badge,
    Table,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from "reactstrap";
import Swal from "sweetalert2";
import apiClient from "api/ApiClient.js";

const EligibilityCheckerTab = () => {
    // State variables
    const [eligibilityRoom, setEligibilityRoom] = useState("");
    const [eligibilityType, setEligibilityType] = useState("Cross-country");
    const [eligibilityCountryTags, setEligibilityCountryTags] = useState("");
    const [eligibilityCityTags, setEligibilityCityTags] = useState("");
    const [eligibilityLanguageTags, setEligibilityLanguageTags] = useState("");
    const [eligibilityGeoScope, setEligibilityGeoScope] = useState("Cross-border");
    const [eligibilityVisibility, setEligibilityVisibility] = useState("Public");
    const [eligibilityCrossCountryFlag, setEligibilityCrossCountryFlag] = useState(true);
    const [eligibilityFlag, setEligibilityFlag] = useState(true);
    const [eligibilityResult, setEligibilityResult] = useState(null);
    const [eligibilityLoading, setEligibilityLoading] = useState(false);
    const [roomLookupLoading, setRoomLookupLoading] = useState(false);
    const [roomFoundInDb, setRoomFoundInDb] = useState(null);
    const [roomsList, setRoomsList] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    // Dynamic database rooms and selected room tracking
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [allRooms, setAllRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);
    const [activeReportTab, setActiveReportTab] = useState("liveDrops");

    // Realistic simulation of silent scoring gap event drops
    const [droppedLogs, setDroppedLogs] = useState([
        {
            time: "5 minutes ago",
            roomName: "Asia Partners Chat",
            roomId: "room-uuid-asia-1",
            eventType: "Active Days of Cross-Country Interaction",
            reason: "Missing cross-country eligibility flag",
            details: {
                type: "Cross-country",
                countryTags: "Japan, Korea",
                cityLangTags: "Tokyo, Japanese",
                visibility: "Public",
                crossCountryFlag: false,
                eligible: true
            }
        },
        {
            time: "25 minutes ago",
            roomName: "NYC Investor Pitch",
            roomId: "room-uuid-nyc-2",
            eventType: "Distinct Cross-Country Conversations",
            reason: "Visibility must be Public or Discoverable (is 'Private')",
            details: {
                type: "Pitch room",
                countryTags: "US, UK",
                cityLangTags: "New York, English",
                visibility: "Private",
                crossCountryFlag: false,
                eligible: true
            }
        },
        {
            time: "1 hour ago",
            roomName: "Global Tech Hub",
            roomId: "room-uuid-tech-3",
            eventType: "Active Days of Cross-Country Interaction",
            reason: "Requires 2 or more distinct country tags (only 'Germany' tagged)",
            details: {
                type: "Cross-country",
                countryTags: "Germany",
                cityLangTags: "Berlin, German",
                visibility: "Public",
                crossCountryFlag: true,
                eligible: true
            }
        },
        {
            time: "3 hours ago",
            roomName: "Alpha Stealth Project",
            roomId: "room-uuid-stealth-4",
            eventType: "Upvote Scoring Signal",
            reason: "Reputation-Eligibility flag set to 'False' in DB",
            details: {
                type: "Innovation board",
                countryTags: "UK",
                cityLangTags: "London, English",
                visibility: "Discoverable",
                crossCountryFlag: false,
                eligible: false
            }
        }
    ]);

    // Helpers to resolve scope & flags automatically based on metadata standards
    const updateGeoScopeAndFlags = (type) => {
        if (type === "Cross-country") {
            setEligibilityGeoScope("Cross-border");
            setEligibilityCrossCountryFlag(true);
        } else if (type === "Country-specific") {
            setEligibilityGeoScope("National");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "City") {
            setEligibilityGeoScope("Local");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "Innovation board") {
            setEligibilityGeoScope("Local");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "Pitch room") {
            setEligibilityGeoScope("Cross-border");
            setEligibilityCrossCountryFlag(false);
        }
    };

    const updateVisibilityFlags = (visibility) => {
        const isEligible = visibility === "Public" || visibility === "Discoverable";
        setEligibilityFlag(isEligible);
    };

    // Automatically manage room type and cross-country flags based on tag counts to avoid mismatches
    useEffect(() => {
        const cTagList = eligibilityCountryTags
            ? eligibilityCountryTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t)
            : [];
        const uniqueCountries = new Set(cTagList);

        if (eligibilityType === "Cross-country" || eligibilityType === "Country-specific" || !eligibilityType) {
            if (uniqueCountries.size >= 2) {
                setEligibilityType("Cross-country");
                setEligibilityGeoScope("Cross-border");
                setEligibilityCrossCountryFlag(true);
            } else {
                setEligibilityType("Country-specific");
                setEligibilityGeoScope("National");
                setEligibilityCrossCountryFlag(false);
            }
        } else {
            setEligibilityCrossCountryFlag(false);
        }
    }, [eligibilityCountryTags, eligibilityType]);

    // Load rooms from database to populate report matrix
    const fetchAllRooms = async () => {
        setRoomsLoading(true);
        try {
            const res = await apiClient.get("/private/get-rooms?limit=100");
            const rooms = res?.data?.payload?.rooms || [];
            setAllRooms(rooms);
        } catch (e) {
            console.error("Error fetching all rooms:", e);
        } finally {
            setRoomsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllRooms();
    }, []);

    // Debounced room name lookup — fetches matching rooms for dropdown selector
    useEffect(() => {
        if (!eligibilityRoom || eligibilityRoom.trim().length < 1) {
            setRoomsList([]);
            setShowDropdown(false);
            setRoomFoundInDb(null);
            setSelectedRoomId(null);
            return;
        }
        setRoomLookupLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.get(`/private/get-rooms?search=${encodeURIComponent(eligibilityRoom)}&limit=10`);
                const rooms = res?.data?.payload?.rooms || [];
                setRoomsList(rooms);
                if (rooms.length > 0) {
                    const exactMatch = rooms.find(r =>
                        (r.name || r.r_name || "").toLowerCase() === eligibilityRoom.trim().toLowerCase()
                    );
                    if (exactMatch) {
                        setRoomFoundInDb('found');
                        setSelectedRoomId(exactMatch.id || exactMatch.r_roomuuid || exactMatch._id);
                    } else {
                        setRoomFoundInDb('not_found');
                        setSelectedRoomId(null);
                    }
                } else {
                    setRoomFoundInDb('not_found');
                    setSelectedRoomId(null);
                }
            } catch (e) {
                console.error("Room lookup error:", e);
                setRoomFoundInDb('not_found');
                setSelectedRoomId(null);
            } finally {
                setRoomLookupLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [eligibilityRoom]);

    // Handle selecting room from search dropdown list
    const handleSelectRoom = (room) => {
        const rName = room.name || room.r_name || "";
        setEligibilityRoom(rName);

        const validTypes = ["Cross-country", "Country-specific", "City", "Innovation board", "Pitch room"];
        const rType = room.type || room.r_type || "Cross-country";
        const normalizedType = validTypes.includes(rType) ? rType : "Cross-country";
        setEligibilityType(normalizedType);
        updateGeoScopeAndFlags(normalizedType);

        setEligibilityCountryTags(room.country || room.r_country || "");
        const rawCity = room.city || room.r_city || "";
        if (rawCity.includes(",")) {
            const parts = rawCity.split(",");
            setEligibilityCityTags(parts[0].trim());
            setEligibilityLanguageTags(parts[1].trim());
        } else {
            setEligibilityCityTags(rawCity);
            setEligibilityLanguageTags("English");
        }

        const rVisibility = room.visibility || room.r_visibility || "Public";
        setEligibilityVisibility(rVisibility);
        updateVisibilityFlags(rVisibility);

        const rEligible = room.eligible !== undefined ? room.eligible : (room.r_eligible !== undefined ? room.r_eligible : true);
        setEligibilityFlag(rEligible);

        setSelectedRoomId(room.id || room.r_roomuuid || room._id);
        setRoomFoundInDb('found');
        setShowDropdown(false);
    };

    // Real-time client-side and backend validation logic for immediate UI state feedback
    useEffect(() => {
        if (!eligibilityRoom || eligibilityRoom.trim() === "") {
            setEligibilityResult(null);
            return;
        }

        const errors = [];

        // 1. Room Type classified validation
        if (!eligibilityType) {
            errors.push("Room type must be classified (e.g. cross-country, city, innovation board).");
        }

        // 2. Cross-country tag check (Requires >= 2 country tags and cross-country flag)
        if (eligibilityType === "Cross-country") {
            const cTagList = eligibilityCountryTags
                ? eligibilityCountryTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t)
                : [];
            const uniqueCountries = new Set(cTagList);
            if (uniqueCountries.size < 2) {
                errors.push("Cross-country classification requires 2 or more distinct country tags.");
            }
            if (!eligibilityCrossCountryFlag) {
                errors.push("Cross-Country eligibility flag must be set to true for cross-country rooms.");
            }
        }

        // 3. Geography Scope must match the designated Room Type scope rules
        if (eligibilityType === "Cross-country" && eligibilityGeoScope !== "Cross-border") {
            errors.push("Geography scope must be 'Cross-border' for Cross-country rooms.");
        }
        if (eligibilityType === "Country-specific" && eligibilityGeoScope !== "National") {
            errors.push("Geography scope must be 'National' for Country-specific rooms.");
        }
        if (eligibilityType === "City" && eligibilityGeoScope !== "Local") {
            errors.push("Geography scope must be 'Local' for City rooms.");
        }
        if (eligibilityType === "Innovation board" && eligibilityGeoScope !== "Local") {
            errors.push("Geography scope must be 'Local' for Innovation board rooms.");
        }

        // 4. City or Language Tags populated
        const hasCity = eligibilityCityTags && eligibilityCityTags.trim() !== "";
        const hasLang = eligibilityLanguageTags && eligibilityLanguageTags.trim() !== "";
        if (!hasCity && !hasLang) {
            errors.push("Supporting metadata (either City Tags or Language Tags) must be populated.");
        }

        // 5. Visibility must be Public, Discoverable, or Private
        if (!["Public", "Discoverable", "Private"].includes(eligibilityVisibility)) {
            errors.push("Room visibility must be Public, Discoverable, or Private for reputation tracking.");
        }

        // 6. Reputation-Eligibility flag
        if (!eligibilityFlag) {
            errors.push("Reputation-Eligibility flag must be explicitly marked as eligible.");
        }

        // Debounce backend verification by 350ms to avoid high API load
        const timer = setTimeout(async () => {
            setEligibilityLoading(true);
            try {
                const res = await apiClient.post("/private/rooms/check-eligibility", {
                    name: eligibilityRoom,
                    roomType: eligibilityType,
                    countryTags: eligibilityCountryTags,
                    geoScope: eligibilityGeoScope,
                    crossCountryFlag: eligibilityCrossCountryFlag,
                    cityTags: eligibilityCityTags,
                    languageTags: eligibilityLanguageTags,
                    visibility: eligibilityVisibility,
                    reputationEligibleFlag: eligibilityFlag
                });

                const payload = res?.data?.payload || res?.data || {};
                const isEligible = payload.isEligible;
                const explanation = payload.explanation;
                const backendErrors = payload.errors || [];
                const matchedRoom = payload.matchedRoom;

                setEligibilityResult({
                    isEligible,
                    explanation,
                    errors: [...new Set([...errors, ...backendErrors])],
                    matchedRoom
                });
            } catch (e) {
                console.error("Backend eligibility check failed:", e);
                setEligibilityResult({
                    isEligible: false,
                    explanation: "Backend check failed: " + (e.response?.data?.message || e.message),
                    errors: [...errors, "API connection issue"],
                    matchedRoom: null
                });
            } finally {
                setEligibilityLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [
        eligibilityRoom,
        eligibilityType,
        eligibilityCountryTags,
        eligibilityGeoScope,
        eligibilityCrossCountryFlag,
        eligibilityCityTags,
        eligibilityLanguageTags,
        eligibilityVisibility,
        eligibilityFlag,
        roomFoundInDb
    ]);

    // Hitting backend API for full compliance check
    const handleCheckEligibility = async () => {
        if (!eligibilityRoom) {
            Swal.fire("Error", "Please enter a Room/Channel Name to validate.", "error");
            return;
        }

        setEligibilityLoading(true);
        try {
            const res = await apiClient.post("/private/rooms/check-eligibility", {
                name: eligibilityRoom,
                roomType: eligibilityType,
                countryTags: eligibilityCountryTags,
                geoScope: eligibilityGeoScope,
                crossCountryFlag: eligibilityCrossCountryFlag,
                cityTags: eligibilityCityTags,
                languageTags: eligibilityLanguageTags,
                visibility: eligibilityVisibility,
                reputationEligibleFlag: eligibilityFlag
            });

            const payload = res?.data?.payload || res?.data || {};
            const isEligible = payload.isEligible;
            const explanation = payload.explanation;
            const errors = payload.errors || [];
            const matchedRoom = payload.matchedRoom;

            setEligibilityResult({
                isEligible,
                explanation,
                errors,
                matchedRoom
            });

            Swal.fire({
                title: isEligible ? "Validation Passed" : "Validation Failed",
                text: isEligible
                    ? "This room passes all system metadata validations and is eligible for scoring."
                    : "This room fails metadata eligibility checks. All reputation events will be dropped.",
                icon: isEligible ? "success" : "warning",
                confirmButtonText: "Ok",
                confirmButtonColor: isEligible ? "#2dce89" : "#f5365c"
            });
        } catch (e) {
            console.error("Backend eligibility check failed:", e);
            Swal.fire("Error", e.response?.data?.message || e.message || "Failed to perform eligibility check", "error");
        } finally {
            setEligibilityLoading(false);
        }
    };

    // Save corrective metadata changes back to the database
    const handleSaveMetadata = async () => {
        if (!selectedRoomId) {
            Swal.fire("Error", "No room selected or found in the database to correct.", "error");
            return;
        }

        setEligibilityLoading(true);
        try {
            await apiClient.patch(`/private/update-room-metadata/${selectedRoomId}`, {
                r_type: eligibilityType,
                r_country: eligibilityCountryTags,
                r_city: `${eligibilityCityTags}, ${eligibilityLanguageTags}`.trim().replace(/^,\s*|\s*,\s*$/, ""),
                r_visibility: eligibilityVisibility,
                r_eligible: eligibilityFlag,
            });

            // If this room was in the mock dropped logs, simulate removing/mitigating it
            setDroppedLogs(prev => prev.filter(log => log.roomName.toLowerCase() !== eligibilityRoom.toLowerCase()));

            Swal.fire({
                title: "Metadata Corrected!",
                html: "<b>Room metadata updated in database.</b><br/>Scoring signals originating from this channel will build reputation score from this correction date forward.<br/><small class='text-warning mt-2 d-block'><i class='fas fa-exclamation-triangle mr-1'></i>Note: Dropped events during the ineligible gap are not retroactively backfilled.</small>",
                icon: "success",
                confirmButtonColor: "#2dce89"
            });

            fetchAllRooms();
        } catch (e) {
            console.error("Save metadata error:", e);
            Swal.fire("Error", e.response?.data?.message || e.message || "Failed to save room metadata.", "error");
        } finally {
            setEligibilityLoading(false);
        }
    };

    // Interactive helper: Auto-populate the editor with room details from table/logs to easily fix it
    const handleLoadRoomForCorrection = (roomData) => {
        const rName = roomData.name || roomData.r_name || roomData.roomName || "";
        setEligibilityRoom(rName);

        const rType = roomData.type || roomData.r_type || roomData.details?.type || "Cross-country";
        setEligibilityType(rType);
        updateGeoScopeAndFlags(rType);

        setEligibilityCountryTags(roomData.country || roomData.r_country || roomData.details?.countryTags || "");
        const rawCity = roomData.city || roomData.r_city || roomData.details?.cityLangTags || "";
        if (rawCity.includes(",")) {
            const parts = rawCity.split(",");
            setEligibilityCityTags(parts[0].trim());
            setEligibilityLanguageTags(parts[1].trim());
        } else {
            setEligibilityCityTags(rawCity);
            setEligibilityLanguageTags("English");
        }

        const rVisibility = roomData.visibility || roomData.r_visibility || roomData.details?.visibility || "Public";
        setEligibilityVisibility(rVisibility);
        updateVisibilityFlags(rVisibility);

        const rEligible = roomData.eligible !== undefined ? roomData.eligible : (roomData.r_eligible !== undefined ? roomData.r_eligible : roomData.details?.eligible);
        setEligibilityFlag(rEligible !== undefined ? rEligible : true);

        const ccFlag = roomData.crossCountryFlag !== undefined ? roomData.crossCountryFlag : roomData.details?.crossCountryFlag;
        setEligibilityCrossCountryFlag(ccFlag !== undefined ? ccFlag : true);

        setSelectedRoomId(roomData.id || roomData.r_roomuuid || roomData._id || roomData.roomId || null);
        setRoomFoundInDb('found');

        window.scrollTo({ top: 0, behavior: 'smooth' });

        Swal.fire({
            title: "Room Loaded",
            text: `Metadata for '${rName}' has been loaded into the validator. Adjust and click 'Save Metadata Corrections' below.`,
            icon: "info",
            timer: 2500,
            showConfirmButton: false
        });
    };

    return (
        <div style={{ animation: "fadeIn 0.4s ease-out" }}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .hover-scale {
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .hover-scale:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.08) !important;
                }
                .glow-btn {
                    transition: all 0.3s ease;
                }
                .glow-btn:hover {
                    box-shadow: 0 0 15px rgba(94, 114, 228, 0.4);
                }
                .glow-btn-success:hover {
                    box-shadow: 0 0 15px rgba(45, 206, 137, 0.4);
                }
            `}</style>

            {/* Core Checker Card */}
            <Card className="shadow hover-scale" style={{ borderRadius: "16px", border: "none", overflow: "hidden", marginBottom: "30px" }}>
                <CardHeader className="border-0 bg-white" style={{ borderRadius: "16px 16px 0 0", padding: "24px" }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <div>
                            <h3 className="mb-0 text-dark font-weight-700" style={{ fontSize: "1.35rem", letterSpacing: "-0.5px" }}>
                                <i className="fas fa-shield-halved text-primary mr-2" />
                                Event Eligibility Checker
                            </h3>
                            <p className="text-muted text-sm mt-1 mb-0">
                                Validate and enforce room metadata standards before ingestion into the reputation events pipeline.
                            </p>
                        </div>
                        
                    </div>
                </CardHeader>

                <CardBody className="bg-secondary px-4 py-4" style={{ borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
                    <Row>
                        {/* Interactive Validator Panel */}
                        <Col lg="6" className="mb-4 mb-lg-0">
                            <div className="bg-white p-4 rounded-lg shadow-sm hover-scale h-100" style={{ border: "1px solid rgba(0,0,0,0.03)", borderRadius: "12px" }}>
                                <FormGroup className="position-relative">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Label className="font-weight-bold text-dark text-sm mb-0">Room / Channel Name</Label>
                                        {roomLookupLoading && (
                                            <span className="text-xs text-muted">
                                                <i className="fas fa-spinner fa-spin mr-1 text-primary" /> Searching DB...
                                            </span>
                                        )}
                                        {!roomLookupLoading && roomFoundInDb === 'found' && (
                                            <span className="text-xs font-weight-bold text-success">
                                                <i className="fas fa-check-circle mr-1" /> Active Room in DB
                                            </span>
                                        )}
                                        {!roomLookupLoading && roomFoundInDb === 'not_found' && (
                                            <span className="text-xs text-warning font-weight-bold">
                                                <i className="fas fa-plus-circle mr-1" /> Unsaved Room
                                            </span>
                                        )}
                                    </div>
                                    <Input
                                        type="text"
                                        placeholder="Type room name or select from monitoring..."
                                        value={eligibilityRoom}
                                        onChange={(e) => {
                                            setEligibilityRoom(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        style={{
                                            borderRadius: "8px",
                                            borderColor: roomFoundInDb === 'found' ? '#2dce89' : roomFoundInDb === 'not_found' ? '#fb6340' : '#dee2e6',
                                            boxShadow: roomFoundInDb === 'found' ? '0 0 0 3px rgba(45,206,137,0.15)' : undefined
                                        }}
                                    />
                                    {showDropdown && roomsList.length > 0 && (
                                        <div className="position-absolute w-100 bg-white border rounded-lg shadow-lg mt-1" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', border: "1px solid rgba(0,0,0,0.1)" }}>
                                            {roomsList.map((room, idx) => (
                                                <div
                                                    key={idx}
                                                    className="px-3 py-2 cursor-pointer dropdown-item hover-scale"
                                                    style={{ cursor: 'pointer', borderBottom: '1px solid #f8f9fe' }}
                                                    onMouseDown={() => handleSelectRoom(room)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="font-weight-bold text-dark text-sm">{room.name || room.r_name}</span>
                                                        <Badge color="info" className="text-capitalize" style={{ fontSize: '9px' }}>{room.type || room.r_type || 'Unknown'}</Badge>
                                                    </div>
                                                    <small className="text-muted d-block text-truncate" style={{ maxWidth: '100%' }}>{room.description || room.r_description || 'No description'}</small>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold text-dark text-sm">Room/Channel Type</Label>
                                    <Input
                                        type="select"
                                        value={eligibilityType}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEligibilityType(val);
                                            updateGeoScopeAndFlags(val);
                                        }}
                                        style={{ borderRadius: "8px" }}
                                    >
                                        <option value="Cross-country">Cross-country</option>
                                        <option value="Country-specific">Country-specific</option>
                                        <option value="City">City</option>
                                        <option value="Innovation board">Innovation board</option>
                                        <option value="Pitch room">Pitch room</option>
                                    </Input>
                                    {eligibilityType === "Innovation board" && (
                                        <small className="text-muted d-block mt-2 font-italic bg-light p-2 rounded" style={{ borderLeft: "3px solid #11cdef" }}>
                                            <i className="fas fa-info-circle text-info mr-1" />
                                            <b>Upvote Limits:</b> Innovation board rooms only accept upvotes as reputation scoring signals.
                                        </small>
                                    )}
                                    {eligibilityType === "Pitch room" && (
                                        <small className="text-muted d-block mt-2 font-italic bg-light p-2 rounded" style={{ borderLeft: "3px solid #11cdef" }}>
                                            <i className="fas fa-info-circle text-info mr-1" />
                                            <b>Pitch Hub:</b> Confines reputation signals solely to verified funding and pitch activities.
                                        </small>
                                    )}
                                </FormGroup>

                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className="font-weight-bold text-dark text-sm">Country Tags</Label>
                                            <Input
                                                type="text"
                                                placeholder="e.g. SG, MY (Comma separated)"
                                                value={eligibilityCountryTags}
                                                onChange={(e) => setEligibilityCountryTags(e.target.value)}
                                                style={{ borderRadius: "8px" }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="font-weight-bold text-dark text-sm">City Tags</Label>
                                            <Input
                                                type="text"
                                                placeholder="e.g. Tokyo"
                                                value={eligibilityCityTags}
                                                onChange={(e) => setEligibilityCityTags(e.target.value)}
                                                style={{ borderRadius: "8px" }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="font-weight-bold text-dark text-sm">Language Tags</Label>
                                            <Input
                                                type="text"
                                                placeholder="e.g. English"
                                                value={eligibilityLanguageTags}
                                                onChange={(e) => setEligibilityLanguageTags(e.target.value)}
                                                style={{ borderRadius: "8px" }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="font-weight-bold text-dark text-sm">Geography Scope</Label>
                                            <Input
                                                type="select"
                                                value={eligibilityGeoScope}
                                                onChange={(e) => setEligibilityGeoScope(e.target.value)}
                                                style={{ borderRadius: "8px" }}
                                            >
                                                <option value="Cross-border">Cross-border</option>
                                                <option value="National">National</option>
                                                <option value="Local">Local</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="font-weight-bold text-dark text-sm">Visibility</Label>
                                            <Input
                                                type="select"
                                                value={eligibilityVisibility}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setEligibilityVisibility(val);
                                                    updateVisibilityFlags(val);
                                                }}
                                                style={{ borderRadius: "8px" }}
                                            >
                                                <option value="Public">Public</option>
                                                <option value="Private">Private</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="mb-3 mt-1 bg-light p-3 rounded-lg mx-0" style={{ border: "1px dashed rgba(0,0,0,0.08)" }}>
                                    <Col md="6" className="py-1">
                                        <CustomInput
                                            type="switch"
                                            id="eligibilityCrossCountrySwitch"
                                            label={<span className="font-weight-bold text-dark text-xs ml-1">Cross-Country Flag</span>}
                                            checked={eligibilityCrossCountryFlag}
                                            disabled={eligibilityType === "Cross-country" && (new Set((eligibilityCountryTags || "").split(',').map(t => t.trim().toLowerCase()).filter(t => t)).size < 2)}
                                            onChange={(e) => setEligibilityCrossCountryFlag(e.target.checked)}
                                        />
                                    </Col>
                                    <Col md="6" className="py-1">
                                        <CustomInput
                                            type="switch"
                                            id="eligibilitySwitch"
                                            label={<span className="font-weight-bold text-dark text-xs ml-1">Reputation-Eligible Flag</span>}
                                            checked={eligibilityFlag}
                                            onChange={(e) => setEligibilityFlag(e.target.checked)}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    {roomFoundInDb === 'found' && (
                                        <Col md="12" className="mb-2">
                                            <Button
                                                color="success"
                                                onClick={handleSaveMetadata}
                                                disabled={eligibilityLoading}
                                                className="w-100 glow-btn-success"
                                                style={{ borderRadius: "8px", fontWeight: "600", padding: "11px 0" }}
                                            >
                                                <i className="fas fa-save mr-2" /> Save Corrections
                                            </Button>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Col>

                        {/* Real-time Checklist & Ingestion Gate Status */}
                        <Col lg="6">
                            <div className="bg-white p-4 rounded-lg shadow-sm hover-scale h-100 d-flex flex-column" style={{ border: "1px solid rgba(0,0,0,0.03)", borderRadius: "12px" }}>
                                {eligibilityResult ? (
                                    <div style={{ animation: "fadeIn 0.3s ease-out" }}>
                                        {/* Status Header */}
                                        <div className="text-center mb-4">
                                            <div style={{
                                                width: '64px', height: '64px', borderRadius: '50%',
                                                background: eligibilityResult.isEligible ? 'linear-gradient(135deg, #2dce89, #2dce89aa)' : 'linear-gradient(135deg, #f5365c, #f5365caa)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                margin: '0 auto 12px',
                                                boxShadow: `0 6px 20px ${eligibilityResult.isEligible ? 'rgba(45,206,137,0.35)' : 'rgba(245,54,92,0.35)'}`
                                            }}>
                                                <i className={`fas fa-${eligibilityResult.isEligible ? 'shield-check' : 'shield-exclamation'} text-white fa-2x`} />
                                            </div>
                                            <h4 className={`font-weight-bold mb-1 ${eligibilityResult.isEligible ? "text-success" : "text-danger"}`} style={{ fontSize: "1.15rem" }}>
                                                {eligibilityResult.isEligible ? "✅ Validation Passed" : "❌ Validation Failed"}
                                            </h4>
                                            <Badge color={eligibilityResult.isEligible ? "success" : "danger"} pill className="text-uppercase" style={{ fontSize: "9px", padding: "4px 8px" }}>
                                                {eligibilityResult.isEligible ? "EVENTS WILL BE INGESTED" : "EVENTS WILL BE DROPPED"}
                                            </Badge>
                                        </div>

                                        {/* Dynamic Response Box */}
                                        <div className="mb-4 p-3 rounded" style={{
                                            background: eligibilityResult.isEligible ? '#f0fdf4' : '#fff5f5',
                                            border: `1px solid ${eligibilityResult.isEligible ? '#bbf7d0' : '#fee2e2'}`
                                        }}>
                                            <div className="d-flex">
                                                <i className={`fas fa-${eligibilityResult.isEligible ? 'info-circle text-success' : 'exclamation-circle text-danger'} mr-2 mt-1`} style={{ fontSize: "16px" }} />
                                                <span className="text-sm text-dark font-weight-500 leading-relaxed" style={{ fontSize: "0.85rem" }}>
                                                    {eligibilityResult.explanation}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Interactive Checklist Report */}
                                        <div className="mb-3">
                                            <h6 className="text-uppercase text-muted ls-1 mb-3" style={{ fontSize: '10px', letterSpacing: '1px', fontWeight: "700" }}>
                                                <i className="fas fa-list-check mr-2 text-primary" />
                                                Pipeline Standards Status
                                            </h6>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">1. Room Type Classified</span>
                                                <Badge color={eligibilityType ? "success" : "danger"} pill style={{ fontSize: "8px" }}>
                                                    {eligibilityType ? "PASSED" : "FAILED"}
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">2. Geographic Scope Matches Type</span>
                                                <Badge color={
                                                    (eligibilityType === "Cross-country" && eligibilityGeoScope === "Cross-border") ||
                                                        (eligibilityType === "Country-specific" && eligibilityGeoScope === "National") ||
                                                        (eligibilityType === "City" && eligibilityGeoScope === "Local") ||
                                                        (eligibilityType === "Innovation board" && eligibilityGeoScope === "Local") ||
                                                        (eligibilityType === "Pitch room" && eligibilityGeoScope === "Cross-border")
                                                        ? "success" : "danger"
                                                } pill style={{ fontSize: "8px" }}>
                                                    {((eligibilityType === "Cross-country" && eligibilityGeoScope === "Cross-border") ||
                                                        (eligibilityType === "Country-specific" && eligibilityGeoScope === "National") ||
                                                        (eligibilityType === "City" && eligibilityGeoScope === "Local") ||
                                                        (eligibilityType === "Innovation board" && eligibilityGeoScope === "Local") ||
                                                        (eligibilityType === "Pitch room" && eligibilityGeoScope === "Cross-border")) ? "PASSED" : "FAILED"}
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">3. Country Tags Count (Cross-Country &ge; 2)</span>
                                                <Badge color={
                                                    eligibilityType !== "Cross-country" ||
                                                        (eligibilityCountryTags && new Set(eligibilityCountryTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t)).size >= 2)
                                                        ? "success" : "danger"
                                                } pill style={{ fontSize: "8px" }}>
                                                    {eligibilityType !== "Cross-country" ||
                                                        (eligibilityCountryTags && new Set(eligibilityCountryTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t)).size >= 2)
                                                        ? "PASSED" : "FAILED"}
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">4. Metadata Labels Populated (City or Lang)</span>
                                                <Badge color={
                                                    (eligibilityCityTags && eligibilityCityTags.trim() !== "") ||
                                                    (eligibilityLanguageTags && eligibilityLanguageTags.trim() !== "")
                                                        ? "success" : "danger"
                                                } pill style={{ fontSize: "8px" }}>
                                                    {
                                                        (eligibilityCityTags && eligibilityCityTags.trim() !== "") ||
                                                        (eligibilityLanguageTags && eligibilityLanguageTags.trim() !== "")
                                                            ? "PASSED" : "FAILED"
                                                    }
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">5. Visibility State (Public, Discoverable, Private)</span>
                                                <Badge color={["Public", "Discoverable", "Private"].includes(eligibilityVisibility) ? "success" : "danger"} pill style={{ fontSize: "8px" }}>
                                                    {["Public", "Discoverable", "Private"].includes(eligibilityVisibility) ? "PASSED" : "FAILED"}
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2 justify-content-between p-2 rounded" style={{ background: '#f8f9fe' }}>
                                                <span className="text-xs font-weight-bold text-dark">6. Reputation Ingestion Flag Enabled</span>
                                                <Badge color={eligibilityFlag ? "success" : "danger"} pill style={{ fontSize: "8px" }}>
                                                    {eligibilityFlag ? "ENABLED" : "DISABLED"}
                                                </Badge>
                                            </div>
                                        </div>

                                        {/* Reset */}
                                        <div className="text-center mt-3">
                                            <Button color="light" size="sm" onClick={() => setEligibilityResult(null)} style={{ borderRadius: "6px" }}>
                                                <i className="fas fa-sync mr-1" /> Reset & Re-validate
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column justify-content-center align-items-center text-center h-100" style={{ minHeight: '320px' }}>
                                        <div className="bg-light rounded-circle p-4 mb-3" style={{ border: "1px dashed rgba(0,0,0,0.06)" }}>
                                            <i className="fas fa-clipboard-check fa-3x text-muted" />
                                        </div>
                                        <h4 className="text-dark font-weight-700 mb-2" style={{ fontSize: "1rem" }}>Awaiting Pipe Compliance Check</h4>
                                        <p className="text-muted text-sm px-3 mb-0" style={{ maxWidth: "380px" }}>
                                            Input room metadata to simulate reputation event drops in real-time.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

        </div>
    );
};

export default EligibilityCheckerTab;
